import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Chip,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Divider,
} from '@mui/material';
import {
  Visibility as ViewIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { payrollService } from '../services/payrollService';
import { employeeService } from '../services/employeeService';
import { useAuth } from '../context/AuthContext';
import { Payroll, Employee } from '../types';
import { formatCurrency } from '../utils/currencyutils';
import { formatDate } from '../utils/dateUtils';
import { payslipPdfService } from '../services/payslipPdfService';
import { companySettingsService } from '../services/companySettingsService';

const MyPayslips: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [payrolls, setPayrolls] = useState<Payroll[]>([]);
  const [employee, setEmployee] = useState<Employee | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedPayroll, setSelectedPayroll] = useState<Payroll | null>(null);
  const [openDialog, setOpenDialog] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      if (!user?.employee_id) return;
      
      try {
        setLoading(true);
        const [payrollData, employeeData] = await Promise.all([
          payrollService.getByEmployeeId(user.employee_id),
          employeeService.getById(user.employee_id)
        ]);
        setPayrolls(payrollData);
        setEmployee(employeeData);
      } catch (error) {
        console.error('Error fetching payrolls:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user?.employee_id]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewPayslip = (payroll: Payroll) => {
    setSelectedPayroll(payroll);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedPayroll(null);
  };

  const getStatusColor = () => {
    return 'success'; // All payrolls are considered processed
  };

  const paginatedPayrolls = payrolls.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Typography variant="h4" gutterBottom component="div">
        My Payslips
      </Typography>

      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Pay Period</TableCell>
                <TableCell>Payment Date</TableCell>
                <TableCell align="right">Gross Pay</TableCell>
                <TableCell align="right">Deductions</TableCell>
                <TableCell align="right">Net Pay</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedPayrolls.map((payroll) => (
                <TableRow hover key={payroll.id}>
                  <TableCell>
                    {formatDate(payroll.pay_period_start)} - {formatDate(payroll.pay_period_end)}
                  </TableCell>
                  <TableCell>{formatDate(payroll.payment_date)}</TableCell>
                  <TableCell align="right">
                    {formatCurrency(payroll.basic_salary + payroll.overtime_pay + payroll.bonuses)}
                  </TableCell>
                  <TableCell align="right">
                    {formatCurrency(payroll.total_deductions + payroll.withholding_tax)}
                  </TableCell>
                  <TableCell align="right">
                    {formatCurrency(payroll.net_pay)}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label="Processed"
                      color={getStatusColor() as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Button
                      startIcon={<ViewIcon />}
                      onClick={() => handleViewPayslip(payroll)}
                      size="small"
                    >
                      View
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={payrolls.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* Payslip Detail Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          Payslip Details - {selectedPayroll && formatDate(selectedPayroll.payment_date)}
        </DialogTitle>
        <DialogContent>
          {selectedPayroll && (
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="h6" gutterBottom>
                  Pay Period
                </Typography>
                <Typography variant="body1">
                  {formatDate(selectedPayroll.pay_period_start)} - {formatDate(selectedPayroll.pay_period_end)}
                </Typography>
                
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  Earnings
                </Typography>
                <Box sx={{ pl: 2 }}>
                  <Typography variant="body2">
                    Basic Salary: {formatCurrency(selectedPayroll.basic_salary)}
                  </Typography>
                  <Typography variant="body2">
                    Overtime Pay: {formatCurrency(selectedPayroll.overtime_pay)}
                  </Typography>
                  <Typography variant="body2">
                    Bonuses: {formatCurrency(selectedPayroll.bonuses)}
                  </Typography>
                  {selectedPayroll.thirteenth_month_pay && selectedPayroll.thirteenth_month_pay > 0 && (
                    <Typography variant="body2" sx={{ color: 'success.main' }}>
                      13th Month Pay: {formatCurrency(selectedPayroll.thirteenth_month_pay)}
                    </Typography>
                  )}
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="body1" fontWeight="bold">
                    Gross Pay: {formatCurrency(selectedPayroll.gross_pay || (selectedPayroll.basic_salary + selectedPayroll.overtime_pay + selectedPayroll.bonuses + (selectedPayroll.thirteenth_month_pay || 0)))}
                  </Typography>
                </Box>
              </Grid>
              
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="h6" gutterBottom>
                  Deductions
                </Typography>
                <Box sx={{ pl: 2 }}>
                  <Typography variant="body2">
                    Total Deductions: {formatCurrency(selectedPayroll.total_deductions)}
                  </Typography>
                  <Typography variant="body2">
                    Withholding Tax: {formatCurrency(selectedPayroll.withholding_tax)}
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="body1" fontWeight="bold">
                    Total Deductions: {formatCurrency(selectedPayroll.total_deductions + selectedPayroll.withholding_tax)}
                  </Typography>
                </Box>
                
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  Net Pay
                </Typography>
                <Typography variant="h4" color="primary">
                  {formatCurrency(selectedPayroll.net_pay)}
                </Typography>
                
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  Hours Worked
                </Typography>
                <Typography variant="body1">
                  {selectedPayroll.total_hours_worked || 0} hours
                </Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Close</Button>
          <Button 
            startIcon={<DownloadIcon />} 
            variant="contained"
            onClick={async () => {
              if (selectedPayroll && employee) {
                const companyInfo = await companySettingsService.getCompanyInfoForPayslip();
                payslipPdfService.openPayslipPDFInNewTab({
                  payroll: selectedPayroll,
                  employee: employee,
                  companyInfo
                });
              } else {
                console.error('Missing payroll or employee data for PDF generation');
              }
            }}
          >
            Download PDF
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MyPayslips;